import React, { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress,
} from "@mui/material";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";

import { LOYALTY_API_BASE, SECTION_COLORS, REWARD_TYPE_STYLES, REWARD_TYPES } from "../../constants";
import { useBrand } from "../../../../contexts/BrandContext";

const INITIAL_FORM = { name: "", description: "", cost_points: 0, type: "POINTS", validity_days: "", active: true };

const CreateRewardDialog = ({ open, onClose, onCreated }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";
    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (field) => (e) => {
        const value = e.target.type === "number" ? Number(e.target.value) : e.target.value;
        setForm((p) => ({ ...p, [field]: value }));
    };
    const isValid = brandName && form.name.trim() && form.type;

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitting(true);
        setError(null);
        try {
            const body = {
                brand: brandName,
                name: form.name.trim(),
                description: form.description.trim() || undefined,
                cost_points: form.cost_points || 0,
                type: form.type,
                validity_days: form.validity_days !== "" ? Number(form.validity_days) : null,
                active: form.active,
            };
            const res = await fetch(`${LOYALTY_API_BASE}/rewards`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": brandName
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
            onCreated();
            handleClose();
        } catch (err) { setError(err.message); } finally { setSubmitting(false); }
    };

    const handleClose = () => { setForm({ ...INITIAL_FORM }); setError(null); onClose(); };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.rewards.gradient }}>
                        <CardGiftcardIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                        Nouvelle Récompense
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                {!brandName && <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>Veuillez sélectionner une marque.</Alert>}
                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={1.5} py={1}
                    sx={{ borderRadius: 2, bgcolor: "#f8f9ff", border: "1px solid #e0e7ff" }}>
                    <StorefrontIcon sx={{ fontSize: 18, color: SECTION_COLORS.rewards.main }} />
                    <Typography fontSize={13} color="text.secondary">Marque :</Typography>
                    <Chip label={brandName || "Aucune"} size="small"
                        sx={{
                            fontWeight: 700, fontSize: 12,
                            bgcolor: brandName ? `${SECTION_COLORS.rewards.main}15` : "#fff0f0",
                            color: brandName ? SECTION_COLORS.rewards.main : "#DC2626"
                        }} />
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom de la récompense" value={form.name}
                            onChange={handleChange("name")} size="small" disabled={!brandName}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={handleChange("description")} size="small" multiline rows={2} disabled={!brandName}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required select label="Type" value={form.type}
                            onChange={handleChange("type")} size="small" disabled={!brandName}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                            {REWARD_TYPES.map((t) => <MenuItem key={t} value={t}>{REWARD_TYPE_STYLES[t]?.label || t}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Coût (points)" type="number" value={form.cost_points}
                            onChange={handleChange("cost_points")} size="small" disabled={!brandName}
                            InputProps={{ inputProps: { min: 0 } }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Validité (jours)" type="number" value={form.validity_days}
                            onChange={handleChange("validity_days")} size="small" disabled={!brandName}
                            helperText="Laisser vide = illimité" InputProps={{ inputProps: { min: 1 } }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1", height: "100%" }}>
                            <Typography fontSize={13} fontWeight={600}>Activer</Typography>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                disabled={!brandName}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.rewards.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.rewards.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>
                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    sx={{ background: SECTION_COLORS.rewards.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Création..." : "Créer la Récompense"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateRewardDialog;
